<script setup lang="ts">
import { isAxiosError } from "axios";
import { definePage } from "unplugin-vue-router/runtime";
import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import SsoService, {
  ESsoErrorCode,
  type TSsoOrganization,
  type TSsoTokens,
} from "@/api/models/auth/sso";
import PrimaryButton from "@/components/shared/buttons/PrimaryButton.vue";
import SecondaryButton from "@/components/shared/buttons/SecondaryButton.vue";
import SsoAccountLinkForm from "@/components/models/auth/SsoAccountLinkForm.vue";
import SsoOrganizationLogo from "@/components/models/auth/SsoOrganizationLogo.vue";
import { useCompleteLogin } from "@/composables/models/auth/useCompleteLogin";
import { getHttpErrorStatus } from "@/router/queryErrorRedirect";
import { isSafeInternalReturnPath } from "@/router/utils";
import { useAuthStore } from "@/stores/authStore";

definePage({
  meta: {
    title: "models.auth.actions.logIn",
    layout: "login",
    requiresAuth: false,
  },
});

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const completeLogin = useCompleteLogin();

const { slug, next, error: errorCode } = route.query as Record<string, string | undefined>;
const organization = ref<TSsoOrganization | null>(null);
const failure = ref<ESsoErrorCode | null>(null);
const linkEmail = ref<string | null>(null);

const failureFor401 = (error: unknown) =>
  isAxiosError(error) && error.response?.status === 401
    ? ESsoErrorCode.INVALID_STATE
    : ESsoErrorCode.FAILED;

const finishLogin = async (tokens: TSsoTokens) => {
  try {
    await authStore.loginWithTokens(tokens);
  } catch (error) {
    failure.value = failureFor401(error);
    return;
  }

  await completeLogin(next && isSafeInternalReturnPath(next) ? next : "/");
};

const onLinked = async (tokens: TSsoTokens) => {
  linkEmail.value = null;
  await finishLogin(tokens);
};

const startLink = async () => {
  try {
    const { data } = await SsoService.getLink();
    linkEmail.value = data.email;
  } catch (error) {
    failure.value = failureFor401(error);
  }
};

const loadOrganization = async (organizationSlug: string) => {
  try {
    const { data } = await SsoService.getOrganization(organizationSlug);
    organization.value = data;
  } catch (error) {
    if (getHttpErrorStatus(error) !== 404) {
      logError(error, { context: { feature: "sso", step: "callback-organization" } });
    }
  }
};

onMounted(async () => {
  if (slug) void loadOrganization(slug);

  if (errorCode === ESsoErrorCode.LINK_REQUIRED) {
    await startLink();
    return;
  }

  if (errorCode) {
    failure.value = Object.values(ESsoErrorCode).includes(errorCode as ESsoErrorCode)
      ? (errorCode as ESsoErrorCode)
      : ESsoErrorCode.FAILED;
    return;
  }

  if (!slug) {
    logError(new Error("SSO callback reached without an organization slug"), {
      context: { feature: "sso", step: "callback" },
    });
    failure.value = ESsoErrorCode.FAILED;
    return;
  }

  let tokens: TSsoTokens;
  try {
    ({ data: tokens } = await SsoService.exchange());
  } catch (error) {
    failure.value = failureFor401(error);
    return;
  }

  await finishLogin(tokens);
});
</script>

<template>
  <div
    class="tw:ml-auto! tw:w-full tw:lg:w-1/2 tw:h-full tw:flex tw:items-center tw:justify-center"
  >
    <div class="tw:w-full tw:max-w-sm tw:flex tw:flex-col tw:items-center tw:gap-6 tw:text-center">
      <SsoOrganizationLogo
        v-if="organization"
        :organization="organization"
        class="tw:h-8 tw:mt-6 tw:lg:mt-0"
      />

      <template v-if="failure">
        <v-icon
          icon="mdi-alert-circle-outline"
          color="error"
          size="48"
        />

        <div>
          <div class="tw:font-bold tw:text-2xl tw:!mb-2">
            {{ t("models.auth.sso.failureTitle") }}
          </div>

          <p
            class="text-medium-emphasis"
            data-testid="sso-error-message"
          >
            {{ t(`models.auth.sso.errors.${failure}`) }}
          </p>
        </div>

        <div class="tw:flex tw:flex-col tw:gap-3 tw:w-full">
          <PrimaryButton
            class="tw:w-full"
            data-testid="sso-retry-button"
            @click="router.push(slug ? `/sso/${slug}` : '/auth/login')"
          >
            {{ t("models.auth.sso.backToLogin") }}
          </PrimaryButton>

          <SecondaryButton
            v-if="slug"
            class="tw:w-full"
            data-testid="sso-password-login-button"
            @click="router.push('/auth/login')"
          >
            {{ t("models.auth.sso.usePassword") }}
          </SecondaryButton>
        </div>
      </template>

      <SsoAccountLinkForm
        v-else-if="linkEmail"
        :email="linkEmail"
        :organization-name="organization?.name ?? slug ?? ''"
        @linked="onLinked"
        @failed="failure = $event"
      />

      <template v-else>
        <v-progress-circular
          indeterminate
          color="primary"
          size="48"
        />

        <p class="text-medium-emphasis">
          {{ t("models.auth.sso.processing") }}
        </p>
      </template>
    </div>
  </div>
</template>
